<script setup>
import { ref, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { getKfServicers, addKfServicer, removeKfServicer } from '@/api/weworkKf'
import { getUserList } from '@/api/user'

const props = defineProps({
  show: Boolean,
  account: Object
})

const emit = defineEmits(['update:show'])

// 接待人员列表
const loading = ref(false)
const servicerList = ref([])

// 用户列表（供选择）
const userLoading = ref(false)
const userOptions = ref([])

// 添加接待人员
const selectedUserId = ref(null)
const adding = ref(false)

// 状态配置
const statusConfig = {
  1: { label: '启用', dot: 'bg-emerald-400', text: 'text-emerald-700', bg: 'bg-emerald-50' },
  0: { label: '禁用', dot: 'bg-slate-400', text: 'text-slate-600', bg: 'bg-slate-100' }
}

// 监听弹窗打开
watch(() => props.show, (val) => {
  if (val && props.account?.openKfid) {
    fetchServicers()
    loadUsers()
    selectedUserId.value = null
  }
})

// 加载用户列表
async function loadUsers() {
  if (userOptions.value.length > 0) return
  userLoading.value = true
  try {
    const response = await getUserList({})
    const list = response?.list || response?.records || response?.data || response || []
    userOptions.value = (Array.isArray(list) ? list : []).map(u => ({
      id: u.id,
      name: u.displayName || u.name || u.username,
      avatar: u.avatar
    }))
  } catch {
    // ignore
  } finally {
    userLoading.value = false
  }
}

// 获取接待人员列表
async function fetchServicers() {
  loading.value = true
  try {
    const data = await getKfServicers(props.account.openKfid)
    servicerList.value = data || []
  } catch (error) {
    ElMessage.error('加载接待人员列表失败')
  } finally {
    loading.value = false
  }
}

// 根据userId查找用户名
const getUserName = (userId) => {
  const user = userOptions.value.find(u => u.id === userId)
  return user?.name || null
}

// 添加接待人员
const handleAdd = async () => {
  if (!selectedUserId.value) {
    return ElMessage.warning('请选择接待人员')
  }

  adding.value = true
  try {
    await addKfServicer(props.account.openKfid, selectedUserId.value)
    ElMessage.success('添加成功')
    selectedUserId.value = null
    fetchServicers()
  } catch (error) {
    ElMessage.error(error.message || '添加失败')
  } finally {
    adding.value = false
  }
}

// 移除接待人员
const handleRemove = async (servicer) => {
  try {
    await ElMessageBox.confirm(
      '确定要移除该接待人员吗？',
      '确认移除',
      {
        confirmButtonText: '确认移除',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )
    await removeKfServicer(servicer.id)
    ElMessage.success('移除成功')
    fetchServicers()
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('移除失败')
    }
  }
}

// 格式化时间
const formatTime = (time) => {
  if (!time) return '-'
  return new Date(time).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// 关闭
const handleClose = () => {
  emit('update:show', false)
}
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
        <div class="fixed inset-0 bg-black/50" @click="handleClose"></div>
        <div class="relative min-h-screen flex items-center justify-center p-4">
          <div class="relative bg-white rounded-xl shadow-2xl overflow-hidden max-w-lg w-full max-h-[80vh] flex flex-col">
            <!-- 头部 -->
            <div class="px-6 py-4 border-b border-slate-200 shrink-0">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <img
                    v-if="account?.avatar"
                    :src="account.avatar"
                    :alt="account?.name"
                    class="w-8 h-8 rounded-full object-cover border border-slate-200"
                  />
                  <div v-else class="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                    <svg class="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 class="text-lg font-semibold text-slate-900">接待人员管理</h3>
                    <p class="text-xs text-slate-400">{{ account?.name }}</p>
                  </div>
                </div>
                <button
                  @click="handleClose"
                  class="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            </div>

            <!-- 添加接待人员 -->
            <div class="px-6 py-4 border-b border-slate-100 bg-slate-50/50 shrink-0">
              <label class="block text-sm font-medium text-slate-700 mb-2">添加接待人员</label>
              <div class="flex items-center gap-3">
                <el-select
                  v-model="selectedUserId"
                  filterable
                  clearable
                  placeholder="搜索并选择用户"
                  :loading="userLoading"
                  class="flex-1"
                >
                  <el-option
                    v-for="user in userOptions"
                    :key="user.id"
                    :label="user.name"
                    :value="user.id"
                  >
                    <div class="flex items-center gap-2">
                      <el-avatar :size="24" :src="user.avatar">
                        {{ user.name?.charAt(0) }}
                      </el-avatar>
                      <span>{{ user.name }}</span>
                    </div>
                  </el-option>
                </el-select>
                <button
                  @click="handleAdd"
                  :disabled="!selectedUserId || adding"
                  class="btn-primary"
                >
                  {{ adding ? '添加中...' : '添加' }}
                </button>
              </div>
            </div>

            <!-- 接待人员列表 -->
            <div class="flex-1 overflow-y-auto">
              <!-- 加载中 -->
              <template v-if="loading">
                <div class="p-6 space-y-3">
                  <div v-for="n in 3" :key="n" class="flex items-center gap-3 animate-pulse">
                    <div class="w-10 h-10 bg-slate-200 rounded-full shrink-0"></div>
                    <div class="flex-1">
                      <div class="h-4 bg-slate-200 rounded w-24 mb-1"></div>
                      <div class="h-3 bg-slate-200 rounded w-32"></div>
                    </div>
                  </div>
                </div>
              </template>

              <!-- 空状态 -->
              <div v-else-if="servicerList.length === 0" class="py-12">
                <div class="flex flex-col items-center">
                  <div class="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                    <svg class="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                    </svg>
                  </div>
                  <p class="text-slate-500 text-sm">暂无接待人员</p>
                  <p class="text-slate-400 text-xs mt-1">请在上方选择用户添加</p>
                </div>
              </div>

              <!-- 列表 -->
              <div v-else class="divide-y divide-slate-100">
                <div
                  v-for="servicer in servicerList"
                  :key="servicer.id"
                  class="px-6 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
                >
                  <div class="flex items-center gap-3 min-w-0">
                    <div class="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center shrink-0">
                      <svg class="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                      </svg>
                    </div>
                    <div class="min-w-0">
                      <div class="flex items-center gap-2">
                        <p class="text-sm font-medium text-slate-900 truncate">
                          {{ getUserName(servicer.userId) || servicer.weworkUserid || `用户#${servicer.userId}` }}
                        </p>
                        <span
                          :class="[statusConfig[servicer.status]?.bg, statusConfig[servicer.status]?.text]"
                          class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium shrink-0"
                        >
                          <span :class="statusConfig[servicer.status]?.dot" class="w-1.5 h-1.5 rounded-full"></span>
                          {{ statusConfig[servicer.status]?.label || '未知' }}
                        </span>
                      </div>
                      <p class="text-xs text-slate-400 truncate">
                        添加时间：{{ formatTime(servicer.createTime) }}
                      </p>
                    </div>
                  </div>
                  <button
                    @click="handleRemove(servicer)"
                    class="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer shrink-0"
                    title="移除"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <!-- 底部统计 -->
            <div class="px-6 py-3 border-t border-slate-200 bg-slate-50/50 shrink-0">
              <p class="text-xs text-slate-400">
                共 <span class="font-medium text-slate-600">{{ servicerList.length }}</span> 名接待人员
              </p>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}
.modal-enter-active .relative,
.modal-leave-active .relative {
  transition: transform 0.3s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
.modal-enter-from .relative,
.modal-leave-to .relative {
  transform: scale(0.95);
}
</style>
